import type { LLMResponseJSONSchema } from '~common/llms/types'

export const INVOICE_CLASSIFICATION_MODEL = 'google/gemini-2.5-flash-lite'
export const INVOICE_DATA_EXTRACTION_MODEL = 'google/gemini-2.5-flash-lite'

export const EMAIL_IMAGE_TEXT_EXTRACTION_MODEL = 'openai/gpt-4.1-nano'
export const INVOICE_IMAGE_TEXT_EXTRACTION_MODEL =
    'google/gemini-2.5-flash-lite'

export const EMAIL_IMAGE_TEXT_EXTRACTION_PROMPT = `You are a helpful assistant that extracts the text in images.
You will be given an image of an email and you need to write out the text in the image.
`

export const INVOICE_IMAGE_TEXT_EXTRACTION_PROMPT = `You are a helpful assistant that extracts the text in images.
You will be given an image of an invoice and you need to write out the text in the image.
`

export const INVOICE_CLASSIFICATION_PROMPT = `
You have the job to classify documents. 
1. Identiy if the provided text is likely related to a payment, purchase, travel booking, subscription, ticket, service or other billable items.
2. We are trying to pre-sort, so be liberal with classifying as "Invoice". Only use "Other" if you are really really sure that this is not a match.
3. Only respond with a JSON object with the key "classification" that can only have the following values: "invoice" or "other".
4. Do not return any other text, markdown/markup or other formatting.
`

export const INVOICE_DATA_EXTRACTION_PROMPT = `You are an expert data extraction assistant. Your task is to extract the fields provided in the Structured Output JSON Schema from the provided text.
## INSTRUCTIONS
- If a field is missing or cannot be determined, set its value to null.
- Do not infer the VAT rate or VAT amount if not found.
- Only output a single JSON object, no other text, markdown/markup or other formatting.
- Strictly follow the schema and key names below.

Provided text:
`

export const INVOICE_REASON_PROMPT = `You are given the text of an invoice or receipt and are tasked to summarize it for the purpose of identifying the purpose of the transaction.
Include information relevant to this task such as:
- The product or service purchased
- The type of product or service
- Subscription time frames
- Amounts purchased
Do not include:
- Vendor information
- Address information
- Invoice/receipt number
- Invoice/receipt date
- Amounts pair or VAT information
- Do not include phrases like "The transaction is..." or "This invoie is...". Simply state what the purchased service or product is like "Subscription for Service X" or "Product Y"

Limit it to two sentences and maxium 240 characters.
`

export const INVOICE_DATA_EXTRACTION_JSON_SCHEMA: LLMResponseJSONSchema = {
    name: 'invoice_data_extraction',
    strict: true,
    schema: {
        type: 'object',
        additionalProperties: false,
        required: [
            'vendorName',
            'vendorEmailAddresses',
            'vendorIbans',
            'vendorAddress',
            'vendorCountry',
            'invoiceId',
            'creditCard',
            'date',
            'amount',
            'currency',
            'vatRate',
            'vatAmount',
        ],
        properties: {
            date: {
                type: ['string', 'null'],
                description:
                    'The verbatim/exact text of the date as found in the invoice, do not change it. If you find a date range, only return the start date. If you cannot find a clear invoice date, take the most important date information instead.',
            },
            amount: {
                type: ['number', 'null'],
                description:
                    'The amount of the transaction in the currency of the invoice, or null if not found',
            },
            currency: {
                type: ['string', 'null'],
                description:
                    'The currency of the transaction as ISO 4217 codes. e.g. "EUR", "GBP", "USD" and others. Return currency symbol only if you are not sure about the currency code, or null if not found',
            },
            vatRate: {
                type: ['number', 'null'],
                description:
                    'The VAT rate of the transaction in percentage. Leave empty if not found. Do not infer the VAT rate if not found.',
            },
            vatAmount: {
                type: ['number', 'null'],
                description:
                    'The VAT amount of the transaction in the currency of the invoice. Leave empty if not found. Do not infer the VAT amount if not found.',
            },
            invoiceId: {
                type: ['string', 'null'],
                description:
                    'The invoice/transaction ID, or null if not found. Keep it completely intact including any special characters, spaces, or dashes. e.g. "INV-123456", not "INV 123456".',
            },
            creditCard: {
                type: ['string', 'null'],
                description:
                    'The credit card used (e.g., last 4 digits or card type), or null if not found',
            },
            vendorName: {
                type: ['string', 'null'],
                description:
                    'The name of the company, shop, vendor, or sender, or null if not found',
            },
            vendorAddress: {
                type: ['string', 'null'],
                description:
                    'The address of the shop, vendor, or sender with city, street, and postal code but without the country. Return null if not found.',
            },
            vendorCountry: {
                type: ['string', 'null'],
                description:
                    'The country of the shop, vendor, or sender as a 2 letter ISO code, or null if not found.',
            },
            vendorEmailAddresses: {
                type: 'array',
                description:
                    'All mentions of email addresses in the text that indicate they are from the sender of the invoice',
                items: {
                    type: 'string',
                    description: 'An email address',
                },
            },
            vendorIbans: {
                type: 'array',
                description:
                    'All mentions of IBANs in the text that indicate they are from the sender of the invoice',
                items: {
                    type: 'string',
                    description: 'An IBAN',
                },
            },
        },
    },
}

export const INVOICE_CLASSIFICATION_JSON_SCHEMA: LLMResponseJSONSchema = {
    name: 'invoice_classification',
    strict: true,
    schema: {
        type: 'object',
        additionalProperties: false,
        required: ['classification'],
        properties: {
            classification: {
                type: 'string',
                description:
                    'The classification of the email - either "invoice" or "other", never anything else. If unsure, return "other".',
            },
        },
    },
}

export const EMAIL_CLASSIFICATION_CUTOFF_LENGTH = 500

export const POSSIBLE_MIME_TYPES = [
    'application/pdf',
    'application/octet-stream',
]
